class YmpLightboxController {
  constructor(modal, enhancer) {
    this.modal = modal;
    this.enhancer = enhancer;
    this.zoomLevels = [1, 1.65, 2.5];
    this.zoomIndex = 0;
    this._currentIndex = 0;
    this._touchStartX = 0;
    this._touchDeltaX = 0;
    this._swipeThreshold = 50;
    this._isSliding = false;
    this._panX = 0;
    this._panY = 0;
    this._isPanning = false;
    this._panPointerId = null;
    this._panStartX = 0;
    this._panStartY = 0;
    this._panOriginX = 0;
    this._panOriginY = 0;
    this._panMoved = false;
    this._suppressClick = false;

    modal.dataset.ympLightboxReady = 'true';
    modal.classList.add('ymp-lightbox');
    this._content = modal.querySelector('.product-media-modal__content');
    this._dialog = modal.querySelector('.product-media-modal__dialog');
    this._closeBtn = modal.querySelector('.product-media-modal__toggle');

    this._buildChrome();
    this._initSlideTrack();
    this._bindChrome();
    this._bindSwipe();
    this._bindPan();
    this._bindResize();
    this._patchModal();
  }

  _initSlideTrack() {
    if (!this._content || this._track) return;

    const items = Array.from(this._content.querySelectorAll(':scope > [data-media-id]'));
    if (!items.length) return;

    const track = document.createElement('div');
    track.className = 'ymp-lightbox__track';

    items.forEach((item) => {
      const slide = document.createElement('div');
      slide.className = 'ymp-lightbox__slide';
      slide.dataset.mediaId = item.getAttribute('data-media-id') || '';
      slide.appendChild(item);
      track.appendChild(slide);
    });

    this._content.appendChild(track);
    this._track = track;
  }

  _applyTrackOffset(index, animate = true) {
    if (!this._track) return;

    const items = this._getMediaItems();
    const slide = items[index];
    if (!slide) return;

    if (this._content) this._content.scrollLeft = 0;

    const offset = slide.offsetLeft;

    if (!animate) {
      this._track.classList.add('is-snapping');
      this._track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      requestAnimationFrame(() => {
        this._track.classList.remove('is-snapping');
      });
      return;
    }

    this._track.style.transform = `translate3d(-${offset}px, 0, 0)`;
  }

  _bindResize() {
    if (!this._content) return;

    this._resizeObserver = new ResizeObserver(() => {
      this._applyTrackOffset(this._currentIndex, false);
      if (this.zoomIndex > 0) {
        const img = this._getActiveImage();
        if (!img) return;
        const scale = this.zoomLevels[this.zoomIndex];
        const clamped = this._clampPan(img, scale, this._panX, this._panY);
        this._panX = clamped.x;
        this._panY = clamped.y;
        this._applyImageTransform(img);
      }
    });
    this._resizeObserver.observe(this._content);
  }

  _patchModal() {
    const originalShow = this.modal.show.bind(this.modal);
    const originalHide = this.modal.hide.bind(this.modal);

    this.modal.show = (opener) => {
      originalShow(opener);
      if (this._content) this._content.scrollLeft = 0;
      this._onOpen(opener);
      this.modal.classList.remove('ymp-lightbox--closing');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('ymp-lightbox--visible');
        });
      });
    };

    this.modal.hide = () => {
      if (!this.modal.hasAttribute('open') || this.modal.classList.contains('ymp-lightbox--closing')) return;

      this.modal.classList.add('ymp-lightbox--closing');
      this.modal.classList.remove('ymp-lightbox--visible');
      this._resetZoom();

      window.setTimeout(() => {
        this.modal.classList.remove('ymp-lightbox--closing');
        originalHide();
      }, 420);
    };

    if (this._closeBtn) {
      const closeBtn = this._closeBtn.cloneNode(true);
      this._closeBtn.replaceWith(closeBtn);
      this._closeBtn = closeBtn;
      this._closeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.modal.hide();
      });
    }

    // Dawn closes media-modal on any pointerup outside deferred-media/product-model (includes images).
    this._dialog?.addEventListener('pointerup', (event) => {
      event.stopPropagation();
    });
  }

  _getMediaItems() {
    if (this._track) {
      return Array.from(this._track.querySelectorAll('.ymp-lightbox__slide'));
    }
    return Array.from(this.modal.querySelectorAll('.product-media-modal__content > [data-media-id]'));
  }

  _getMediaIdFromSlide(slide) {
    if (!slide) return null;
    return (
      slide.dataset.mediaId ||
      slide.querySelector('[data-media-id]')?.getAttribute('data-media-id')
    );
  }

  _getMediaIdFromOpener(opener) {
    if (!opener) return null;
    return (
      opener.getAttribute?.('data-media-id') ||
      opener.querySelector?.('[data-media-id]')?.getAttribute('data-media-id') ||
      opener.closest?.('[data-media-id]')?.getAttribute('data-media-id')
    );
  }

  _buildChrome() {
    if (!this._dialog || this._dialog.querySelector('.ymp-lightbox__chrome')) return;

    const chrome = document.createElement('div');
    chrome.className = 'ymp-lightbox__chrome';
    chrome.innerHTML = `
      <div class="ymp-lightbox__tools ymp-lightbox__tools--zoom">
        <button type="button" class="ymp-lightbox__tool" data-ymp-lightbox-zoom-out aria-label="Zoom out">−</button>
        <button type="button" class="ymp-lightbox__tool" data-ymp-lightbox-zoom-in aria-label="Zoom in">+</button>
      </div>
      <div class="ymp-lightbox__footer">
        <div class="ymp-lightbox__dots" data-ymp-lightbox-dots role="tablist"></div>
        <div class="ymp-lightbox__arrows">
          <button type="button" class="ymp-lightbox__arrow" data-ymp-lightbox-prev aria-label="Previous slide">
            <span class="svg-wrapper"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M7.5 10.5L3 6L7.5 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
          </button>
          <button type="button" class="ymp-lightbox__arrow" data-ymp-lightbox-next aria-label="Next slide">
            <span class="svg-wrapper"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 1.5L9 6L4.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
          </button>
        </div>
      </div>
    `;
    this._dialog.appendChild(chrome);
    this._chrome = chrome;
    this._dotsEl = chrome.querySelector('[data-ymp-lightbox-dots]');
    this._rebuildDots();
  }

  _bindChrome() {
    this._chrome?.querySelector('[data-ymp-lightbox-zoom-in]')?.addEventListener('click', () => this._stepZoom(1));
    this._chrome?.querySelector('[data-ymp-lightbox-zoom-out]')?.addEventListener('click', () => this._stepZoom(-1));
    this._chrome?.querySelector('[data-ymp-lightbox-prev]')?.addEventListener('click', () => this._go(-1));
    this._chrome?.querySelector('[data-ymp-lightbox-next]')?.addEventListener('click', () => this._go(1));

    this._content?.addEventListener('click', (event) => {
      if (this._suppressClick) return;
      const img = event.target.closest('img');
      if (!img) return;
      const slide = img.closest('.ymp-lightbox__slide');
      const items = this._getMediaItems();
      if (!slide || items[this._currentIndex] !== slide) return;
      this._toggleZoomOnClick();
    });

    this.modal.addEventListener('keyup', (event) => {
      if (this.zoomIndex > 0) return;
      if (event.code === 'ArrowLeft') this._go(-1);
      if (event.code === 'ArrowRight') this._go(1);
    });
  }

  _bindSwipe() {
    if (!this._content) return;

    this._content.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || this.zoomIndex > 0) return;
        this._touchStartX = event.touches[0].clientX;
        this._touchDeltaX = 0;
      },
      { passive: true }
    );

    this._content.addEventListener(
      'touchmove',
      (event) => {
        if (!this._touchStartX || this.zoomIndex > 0) return;
        this._touchDeltaX = event.touches[0].clientX - this._touchStartX;
      },
      { passive: true }
    );

    this._content.addEventListener(
      'touchend',
      () => {
        if (!this._touchStartX || this.zoomIndex > 0) return;
        if (Math.abs(this._touchDeltaX) >= this._swipeThreshold) {
          this._go(this._touchDeltaX > 0 ? -1 : 1);
        }
        this._touchStartX = 0;
        this._touchDeltaX = 0;
      },
      { passive: true }
    );
  }

  _bindPan() {
    if (!this._content) return;

    const onPointerDown = (event) => {
      if (this.zoomIndex <= 0 || event.button > 0) return;

      const img = event.target.closest('img');
      if (!img || img !== this._getActiveImage()) return;

      this._isPanning = true;
      this._panMoved = false;
      this._panPointerId = event.pointerId;
      this._panStartX = event.clientX;
      this._panStartY = event.clientY;
      this._panOriginX = this._panX;
      this._panOriginY = this._panY;
      img.setPointerCapture(event.pointerId);
      img.classList.add('is-panning');
    };

    const onPointerMove = (event) => {
      if (!this._isPanning || event.pointerId !== this._panPointerId) return;

      const dx = event.clientX - this._panStartX;
      const dy = event.clientY - this._panStartY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this._panMoved = true;

      const img = this._getActiveImage();
      if (!img) return;

      const scale = this.zoomLevels[this.zoomIndex];
      const clamped = this._clampPan(img, scale, this._panOriginX + dx, this._panOriginY + dy);
      this._panX = clamped.x;
      this._panY = clamped.y;
      this._applyImageTransform(img);
    };

    const onPointerEnd = (event) => {
      if (!this._isPanning || event.pointerId !== this._panPointerId) return;

      this._isPanning = false;
      this._panPointerId = null;
      this._getActiveImage()?.classList.remove('is-panning');

      if (this._panMoved) {
        this._suppressClick = true;
        window.setTimeout(() => {
          this._suppressClick = false;
        }, 0);
      }
    };

    this._content.addEventListener('pointerdown', onPointerDown);
    this._content.addEventListener('pointermove', onPointerMove);
    this._content.addEventListener('pointerup', onPointerEnd);
    this._content.addEventListener('pointercancel', onPointerEnd);
  }

  _getPanBounds(img, scale) {
    if (!this._content || !img) return { maxX: 0, maxY: 0 };

    const viewport = this._content.getBoundingClientRect();
    const scaledW = img.offsetWidth * scale;
    const scaledH = img.offsetHeight * scale;

    return {
      maxX: Math.max(0, (scaledW - viewport.width) / 2),
      maxY: Math.max(0, (scaledH - viewport.height) / 2),
    };
  }

  _clampPan(img, scale, panX, panY) {
    const { maxX, maxY } = this._getPanBounds(img, scale);
    return {
      x: Math.min(maxX, Math.max(-maxX, panX)),
      y: Math.min(maxY, Math.max(-maxY, panY)),
    };
  }

  _applyImageTransform(img) {
    if (!img) return;

    const scale = this.zoomLevels[this.zoomIndex];
    if (scale <= 1) {
      img.style.transform = '';
      return;
    }

    img.style.transform = `translate3d(${this._panX}px, ${this._panY}px, 0) scale(${scale})`;
  }

  _rebuildDots() {
    if (!this._dotsEl) return;
    const items = this._getMediaItems();
    this._dotsEl.innerHTML = '';
    if (items.length <= 1) return;

    items.forEach((item, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ymp-lightbox__dot';
      dot.setAttribute('aria-label', `Slide ${index + 1}`);
      dot.addEventListener('click', () => this._setIndex(index));
      this._dotsEl.appendChild(dot);
    });
  }

  _onOpen(opener) {
    const mediaId = this._getMediaIdFromOpener(opener);
    const items = this._getMediaItems();
    const index = items.findIndex((item) => this._getMediaIdFromSlide(item) === String(mediaId));
    this._currentIndex = index >= 0 ? index : 0;
    this._resetZoom();
    this._applyTrackOffset(this._currentIndex, false);
    this._syncUi();
    this._syncGalleryFromLightbox();
  }

  _syncGalleryFromLightbox() {
    const items = this._getMediaItems();
    const item = items[this._currentIndex];
    const mediaId = this._getMediaIdFromSlide(item);
    if (!mediaId || !this.enhancer?.mediaGallery) return;

    const slideMediaId = this.enhancer.slides().find((slide) => {
      const slideId = slide.dataset.mediaId || '';
      return slideId.endsWith(`-${mediaId}`);
    })?.dataset.mediaId;

    if (slideMediaId && typeof this.enhancer.activate === 'function') {
      this.enhancer.activate(slideMediaId);
    }
  }

  _setIndex(index, animate = true) {
    const items = this._getMediaItems();
    if (!items.length || this._isSliding) return;

    if (index < 0) index = items.length - 1;
    if (index >= items.length) index = 0;
    if (index === this._currentIndex) return;

    this._isSliding = true;
    this._currentIndex = index;
    this._resetZoom();
    this._applyTrackOffset(index, animate);
    this._syncUi();
    this._syncGalleryFromLightbox();

    window.setTimeout(() => {
      this._isSliding = false;
    }, animate ? 460 : 0);
  }

  _go(direction) {
    this._setIndex(this._currentIndex + direction);
  }

  _getActiveImage() {
    const slide = this._getMediaItems()[this._currentIndex];
    return slide?.querySelector('img') || null;
  }

  _applyZoom() {
    const img = this._getActiveImage();
    if (!img) return;

    const scale = this.zoomLevels[this.zoomIndex];
    if (this.zoomIndex > 0) {
      const clamped = this._clampPan(img, scale, this._panX, this._panY);
      this._panX = clamped.x;
      this._panY = clamped.y;
    } else {
      this._panX = 0;
      this._panY = 0;
    }

    this._applyImageTransform(img);
    img.style.cursor =
      this.zoomIndex > 0
        ? this._isPanning
          ? 'grabbing'
          : 'grab'
        : this.zoomIndex >= this.zoomLevels.length - 1
          ? 'zoom-out'
          : 'zoom-in';
    this._content?.classList.toggle('ymp-lightbox__content--zoomed', this.zoomIndex > 0);
    this._syncZoomButtons();
  }

  _resetZoom() {
    this.zoomIndex = 0;
    this._panX = 0;
    this._panY = 0;
    this._isPanning = false;
    this._panPointerId = null;
    this._getMediaItems().forEach((item) => {
      item.querySelectorAll('img').forEach((img) => {
        img.style.transform = '';
        img.style.cursor = 'zoom-in';
        img.classList.remove('is-panning');
      });
    });
    this._content?.classList.remove('ymp-lightbox__content--zoomed');
    this._syncZoomButtons();
  }

  _stepZoom(direction) {
    const max = this.zoomLevels.length - 1;
    const next = Math.min(max, Math.max(0, this.zoomIndex + direction));
    if (next === this.zoomIndex) return;
    this.zoomIndex = next;
    this._panX = 0;
    this._panY = 0;
    this._applyZoom();
  }

  _toggleZoomOnClick() {
    const max = this.zoomLevels.length - 1;
    if (this.zoomIndex < max) {
      this.zoomIndex += 1;
    } else {
      this.zoomIndex = 0;
    }
    this._panX = 0;
    this._panY = 0;
    this._applyZoom();
  }

  _syncZoomButtons() {
    const zoomIn = this._chrome?.querySelector('[data-ymp-lightbox-zoom-in]');
    const zoomOut = this._chrome?.querySelector('[data-ymp-lightbox-zoom-out]');
    if (zoomIn) zoomIn.disabled = this.zoomIndex >= this.zoomLevels.length - 1;
    if (zoomOut) zoomOut.disabled = this.zoomIndex <= 0;
  }

  _syncUi() {
    const items = this._getMediaItems();
    const dots = this._dotsEl?.querySelectorAll('.ymp-lightbox__dot') || [];
    dots.forEach((dot, index) => {
      const active = index === this._currentIndex;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const prev = this._chrome?.querySelector('[data-ymp-lightbox-prev]');
    const next = this._chrome?.querySelector('[data-ymp-lightbox-next]');
    const hideNav = items.length <= 1;
    if (prev) prev.hidden = hideNav;
    if (next) next.hidden = hideNav;
    if (this._dotsEl) this._dotsEl.hidden = hideNav;
  }
}
if (!customElements.get('ymp-gallery-enhancer')) {
  class YmpGalleryEnhancer extends HTMLElement {
    constructor() {
      super();
      this.mediaGallery = this.closest('media-gallery');
      this.viewer = this.mediaGallery?.querySelector('[id^="GalleryViewer"]');
      this.track = this.mediaGallery?.querySelector('.ymp-gallery__list');
      this.slides = () => Array.from(this.mediaGallery?.querySelectorAll('.ymp-gallery__slide[data-media-id]') || []);
      this._slideIndex = 0;
      this._isAnimating = false;
    }

    connectedCallback() {
      if (!this.mediaGallery || !this.viewer || !this.track) return;

      this.sectionId = this.mediaGallery.id.replace('MediaGallery-', '');

      this._patchMediaGallery();
      this._bindControls();
      this._bindResize();
      this._bindGalleryHeightSync();

      if (this.mediaGallery.dataset.ympMobileSwipe === 'true') {
        this._bindMobileSwipe();
      }

      this._initLightbox();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._initGalleryState());
      });
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._resizeObserver?.disconnect();
      this._infoResizeObserver?.disconnect();
      this._galleryHeightMql?.removeEventListener('change', this._onGalleryHeightMql);
      window.removeEventListener('resize', this._onGalleryHeightResize);
      this._swipeMql?.removeEventListener('change', this._onSwipeBreakpoint);
      if (this._swipeStage) {
        this._swipeStage.removeEventListener('touchstart', this._onTouchStart);
        this._swipeStage.removeEventListener('touchmove', this._onTouchMove);
        this._swipeStage.removeEventListener('touchend', this._onTouchEnd);
      }
    }

    _patchMediaGallery() {
      if (this.mediaGallery._ympPatched) return;
      this.mediaGallery._ympPatched = true;

      const enhancer = this;

      this.mediaGallery.setActiveMedia = function (mediaId, prepend) {
        const viewer = this.elements?.viewer || enhancer.viewer;
        const activeMedia =
          viewer?.querySelector(`[data-media-id="${mediaId}"]`) || viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          if (viewer.resetPages) viewer.resetPages();
        }

        if (typeof this.preventStickyHeader === 'function') this.preventStickyHeader();
        if (typeof this.playActiveMedia === 'function') this.playActiveMedia(activeMedia);

        if (enhancer.track) enhancer.track.scrollLeft = 0;

        const index = enhancer.slides().indexOf(activeMedia);
        if (index >= 0) {
          enhancer._slideIndex = index;
          enhancer._applyTrackOffset(index);
          enhancer.syncDots(mediaId);
        }
      };

      this._observer = new MutationObserver(() => {
        const current = this.slides().find((slide) => slide.classList.contains('is-active'));
        if (!current) return;
        const index = this.slides().indexOf(current);
        if (index >= 0 && index !== this._slideIndex) {
          this._slideIndex = index;
          if (this.track) this.track.scrollLeft = 0;
          this._applyTrackOffset(index);
        }
        this.syncDots(current.dataset.mediaId);
      });
      this._observer.observe(this.mediaGallery, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    _bindControls() {
      this.mediaGallery.querySelector('[data-ymp-prev]')?.addEventListener('click', () => this.go(-1));
      this.mediaGallery.querySelector('[data-ymp-next]')?.addEventListener('click', () => this.go(1));
      this.mediaGallery.querySelector('[data-ymp-zoom]')?.addEventListener('click', () => this.openZoom());

      this.mediaGallery.querySelectorAll('[data-ymp-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const mediaId = dot.dataset.mediaId;
          if (mediaId) this.activate(mediaId);
        });
      });

      this.mediaGallery.querySelectorAll('.ymp-gallery__slide .product__media img').forEach((img) => {
        img.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const toggle = img.closest('.product__modal-opener')?.querySelector('.product__media-toggle');
          if (toggle) toggle.click();
          else this.openZoom();
        });
      });
    }

    _bindResize() {
      this._resizeObserver = new ResizeObserver(() => {
        this._applyTrackOffset(this._slideIndex, false);
      });
      this._resizeObserver.observe(this.viewer);
    }

    _bindGalleryHeightSync() {
      const layout = this.closest('.ymp__layout');
      const infoCol = layout?.querySelector('.ymp__info-col');
      const root = this.closest('.ymp');
      if (!layout || !infoCol || !root) return;

      this._galleryHeightMql = window.matchMedia('(min-width: 1350px)');
      this._onGalleryHeightMql = () => this._syncGalleryHeight();
      this._galleryHeightMql.addEventListener('change', this._onGalleryHeightMql);

      this._infoResizeObserver = new ResizeObserver(() => {
        if (this._galleryHeightMql.matches) this._syncGalleryHeight();
      });
      this._infoResizeObserver.observe(infoCol);

      this._onGalleryHeightResize = () => {
        if (this._galleryHeightMql.matches) this._syncGalleryHeight();
      };
      window.addEventListener('resize', this._onGalleryHeightResize);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._syncGalleryHeight());
      });
    }

    _getGalleryMaxHeight() {
      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      const safeHeaderHeight = Number.isFinite(headerHeight) ? headerHeight : 0;

      return Math.max(0, window.innerHeight - safeHeaderHeight);
    }

    _syncGalleryHeight() {
      const layout = this.closest('.ymp__layout');
      const infoCol = layout?.querySelector('.ymp__info-col');
      const root = this.closest('.ymp');
      if (!layout || !infoCol || !root) return;

      if (!window.matchMedia('(min-width: 1350px)').matches) {
        root.style.removeProperty('--ymp-gallery-sync-height');
        return;
      }

      const maxHeight = this._getGalleryMaxHeight();
      const syncHeight = Math.min(infoCol.offsetHeight, maxHeight);
      root.style.setProperty('--ymp-gallery-sync-height', `${syncHeight}px`);
      this._applyTrackOffset(this._slideIndex, false);
    }

    _initGalleryState() {
      const slides = this.slides();
      if (!slides.length) return;

      let active = slides.find((slide) => slide.classList.contains('is-active'));
      if (!active) {
        active = slides[0];
        active.classList.add('is-active');
      }

      this._slideIndex = slides.indexOf(active);
      if (this.track) this.track.scrollLeft = 0;
      this._applyTrackOffset(this._slideIndex, false);
      this.syncDots(active.dataset.mediaId);
    }

    _applyTrackOffset(index, animate = true) {
      const slide = this.slides()[index];
      if (!this.track || !slide) return;

      const offset = slide.offsetLeft;

      if (!animate) {
        this.track.classList.add('is-dragging');
        this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
        requestAnimationFrame(() => {
          this.track.classList.remove('is-dragging');
        });
        return;
      }

      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    }

    _bindMobileSwipe() {
      this._swipeStage = this.mediaGallery?.querySelector('.ymp-gallery__stage');
      if (!this._swipeStage) return;

      this._swipeStage.classList.add('ymp-gallery__stage--swipe');
      this._swipeMql = window.matchMedia('(max-width: 749px)');
      this._swipeThreshold = Number(this.mediaGallery.dataset.ympSwipeThreshold) || 50;
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchTracking = false;
      this._touchDeltaX = 0;

      this._onTouchStart = (event) => {
        if (!this._swipeMql.matches || event.touches.length !== 1) return;
        this._touchStartX = event.touches[0].clientX;
        this._touchStartY = event.touches[0].clientY;
        this._touchDeltaX = 0;
        this._touchTracking = true;
      };

      this._onTouchMove = (event) => {
        if (!this._touchTracking || !this._swipeMql.matches) return;
        const touch = event.touches[0];
        const deltaX = touch.clientX - this._touchStartX;
        const deltaY = touch.clientY - this._touchStartY;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this._touchTracking = false;
          return;
        }
        if (Math.abs(deltaX) > 10) event.preventDefault();
        this._touchDeltaX = deltaX;
      };

      this._onTouchEnd = () => {
        if (!this._touchTracking || !this._swipeMql.matches) return;
        this._touchTracking = false;
        if (Math.abs(this._touchDeltaX) < this._swipeThreshold) return;
        this.go(this._touchDeltaX > 0 ? -1 : 1);
      };

      this._onSwipeBreakpoint = () => {
        this._touchTracking = false;
      };

      this._swipeStage.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this._swipeStage.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this._swipeStage.addEventListener('touchend', this._onTouchEnd, { passive: true });
      this._swipeMql.addEventListener('change', this._onSwipeBreakpoint);
    }

    getActiveIndex() {
      const slides = this.slides();
      const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      return index >= 0 ? index : 0;
    }

    go(direction) {
      const slides = this.slides();
      if (slides.length < 2 || this._isAnimating) return;
      let next = this.getActiveIndex() + direction;
      if (next < 0) next = slides.length - 1;
      if (next >= slides.length) next = 0;
      this.activate(slides[next].dataset.mediaId);
    }

    activate(mediaId) {
      const slides = this.slides();
      const nextIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (nextIndex < 0) return;

      this._isAnimating = true;
      window.setTimeout(() => {
        this._isAnimating = false;
      }, 520);

      if (typeof this.mediaGallery.setActiveMedia === 'function') {
        this.mediaGallery.setActiveMedia(mediaId, false);
      } else {
        slides.forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
        });
        this._slideIndex = nextIndex;
        if (this.track) this.track.scrollLeft = 0;
        this._applyTrackOffset(nextIndex);
        this.syncDots(mediaId);
      }
    }

    dots() {
      return Array.from(this.mediaGallery?.querySelectorAll('[data-ymp-dot]') || []);
    }

    syncDots(mediaId) {
      this.dots().forEach((dot) => {
        const active = dot.dataset.mediaId === mediaId;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      const slides = this.slides();
      const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      const prev = this.mediaGallery.querySelector('[data-ymp-prev]');
      const next = this.mediaGallery.querySelector('[data-ymp-next]');
      if (prev) prev.disabled = slides.length <= 1;
      if (next) next.disabled = slides.length <= 1;
      if (index >= 0) {
        const zoom = this.mediaGallery.querySelector('[data-ymp-zoom]');
        if (zoom) {
          zoom.setAttribute(
            'aria-label',
            window.accessibilityStrings?.imageAvailable?.replace('[index]', index + 1) || `Slide ${index + 1}`
          );
        }
      }
    }

    openZoom() {
      const active = this.slides().find((slide) => slide.classList.contains('is-active'));
      if (!active) return;

      const toggle = active.querySelector('.product__media-toggle');
      if (toggle) {
        toggle.click();
        return;
      }

      const openerButton = active.querySelector('modal-opener button');
      openerButton?.click();
    }

    _initLightbox() {
      const modal = document.querySelector(`#ProductModal-${this.sectionId}`);
      if (!modal || modal.dataset.ympLightboxReady === 'true') return;
      this._lightbox = new YmpLightboxController(modal, this);
    }
  }

  customElements.define('ymp-gallery-enhancer', YmpGalleryEnhancer);
}

if (!customElements.get('ymp-post-atc-bar')) {
  class YmpPostAtcBar extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;

      this.sectionId = this.dataset.sectionId;
      this._pendingShow = false;

      this._closeBtn = this.querySelector('[data-ymp-post-atc-close]');
      this._priceEl = this.querySelector('[data-ymp-post-atc-price]');
      this._imageEl = this.querySelector('[data-ymp-post-atc-image]');

      this._closeBtn?.addEventListener('click', () => this.hide());
      this.productInfo.querySelectorAll('product-form').forEach((form) => {
        form.addEventListener('submit', () => {
          this._pendingShow = true;
        });
      });

      if (typeof subscribe === 'function') {
        this._cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!this._pendingShow || event.source !== 'product-form') return;
          this._pendingShow = false;
          this.show();
        });

        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || String(event.data.sectionId) !== String(this.sectionId)) return;
          this._updateFromVariant(event.data.html, event.data.variant);
        });

        this._cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form') this._pendingShow = false;
        });
      }
    }

    disconnectedCallback() {
      this._cartUnsub?.();
      this._variantUnsub?.();
      this._cartErrorUnsub?.();
    }

    show() {
      this.hidden = false;
      this.classList.add('is-visible');
      document.documentElement.classList.add('ymp-post-atc-visible');
    }

    hide() {
      this.hidden = true;
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('ymp-post-atc-visible');
    }

    _updateFromVariant(html, variant) {
      if (this._priceEl && html) {
        const newPrice = html.getElementById(`price-${this.sectionId}`);
        if (newPrice) this._priceEl.innerHTML = newPrice.innerHTML;
      }

      if (!this._imageEl) return;

      if (variant?.featured_media?.preview_image?.src) {
        const preview = variant.featured_media.preview_image;
        const src = preview.src || '';
        if (src) {
          const width = preview.width ? Math.min(preview.width, 160) : 160;
          this._imageEl.src = src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
          if (preview.alt) this._imageEl.alt = preview.alt;
          return;
        }
      }

      if (html) {
        const activeImg = html.querySelector(
          '.ymp-gallery__slide.is-active img, .product__media-item.is-active img, .product__media img'
        );
        if (activeImg?.src) {
          this._imageEl.src = activeImg.currentSrc || activeImg.src;
          if (activeImg.alt) this._imageEl.alt = activeImg.alt;
        }
      }
    }
  }

  customElements.define('ymp-post-atc-bar', YmpPostAtcBar);
}
